import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Loader2, CheckCircle2 } from 'lucide-react';
import client from '../api/client';
import Footer from '../components/Footer';

const TYPES = [
  { value: 'access', label: 'Access — send me a copy of my data' },
  { value: 'delete', label: 'Delete — erase my data' },
  { value: 'correct', label: 'Correct — fix inaccurate data' },
  { value: 'opt_out', label: 'Opt out — stop texts / sale or sharing' },
  { value: 'other', label: 'Something else' },
];

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wide text-[#6A6A6A]">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const inputCls = 'w-full px-3 py-2.5 rounded-xl border border-[#EFE3D8] bg-white text-sm focus:outline-none focus:border-[#7B2C3B]';

export default function PrivacyRequest() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', request_type: 'access', relationship: 'account_holder', details: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ref, setRef] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const { data } = await client.post('/privacy-requests', { ...form, phone: form.phone || null });
      setRef(data.reference);
    } catch (err) {
      setError(err.message || 'Could not submit');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBF7F2]">
      <div className="max-w-2xl mx-auto px-6 py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#7B2C3B] mb-10"><Heart className="w-4 h-4 fill-[#7B2C3B]" /> Your Wedding Friends</Link>
        <p className="text-[11px] uppercase tracking-widest text-[#7B2C3B]">Privacy</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#2A2A2A] mt-2">Make a privacy request</h1>
        <p className="text-sm text-[#6A6A6A] mt-3 max-w-xl">Couples, guests and anyone whose information is on our platform can ask to access, delete, or correct their data, or opt out. We may email you to verify your identity, and respond within 45 days.</p>
        {ref ? (
          <div data-testid="privacy-request-success" className="mt-10 rounded-2xl bg-white border border-[#EFE3D8] p-8">
            <CheckCircle2 className="w-8 h-8 text-[#2F5D2F]" />
            <h2 className="font-serif text-2xl mt-3">Request received</h2>
            <p className="text-sm text-[#6A6A6A] mt-2">Your reference number is <strong data-testid="privacy-request-ref" className="text-[#7B2C3B]">{ref}</strong>. We sent a confirmation to {form.email}.</p>
          </div>
        ) : (
          <form data-testid="privacy-request-form" onSubmit={submit} className="mt-10 rounded-2xl bg-white border border-[#EFE3D8] p-6 sm:p-8 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name"><input data-testid="pr-name" required value={form.name} onChange={set('name')} className={inputCls} /></Field>
              <Field label="Email"><input data-testid="pr-email" type="email" required value={form.email} onChange={set('email')} className={inputCls} /></Field>
            </div>
            <Field label="Mobile number (optional — needed for text opt-outs)"><input data-testid="pr-phone" value={form.phone} onChange={set('phone')} placeholder="+1 555 555 5555" className={inputCls} /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Request type">
                <select data-testid="pr-type" value={form.request_type} onChange={set('request_type')} className={inputCls}>
                  {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="I am a">
                <select data-testid="pr-relationship" value={form.relationship} onChange={set('relationship')} className={inputCls}>
                  <option value="account_holder">Account holder (host)</option>
                  <option value="guest">Guest of an event</option>
                  <option value="other">Other</option>
                </select>
              </Field>
            </div>
            <Field label="Details"><textarea data-testid="pr-details" rows={4} value={form.details} onChange={set('details')} placeholder="Which event or account is this about? Anything that helps us find your data." className={inputCls} /></Field>
            {error && <p data-testid="pr-error" className="text-sm text-red-700">{error}</p>}
            <button data-testid="pr-submit" disabled={busy} className="px-6 py-3 rounded-full bg-[#7B2C3B] text-white text-sm inline-flex items-center gap-2 disabled:opacity-50 hover:bg-[#6A2433] transition-colors">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit request
            </button>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
}
